import {useMemo} from 'react'
import {Link as RouterLink} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Box} from '@mui/material'
import {useAuth} from '../auth/useAuth'
import {useAppTokens, type ThemeTokens} from '../theme'
import BrandLogo from './BrandLogo'
import {GUIDES_INDEX_PATH} from './seo/seoConstants'

type FooterLink = {to: string; label: string}
type FooterColumn = {label: string; links: FooterLink[]}

const LAPTOP_BREAKPOINT = '@media (max-width: 1024px)'
const TABLET_BREAKPOINT = '@media (max-width: 880px)'
const PHONE_BREAKPOINT = '@media (max-width: 480px)'

const Footer = () => {
  const {t} = useTranslation()
  const {user} = useAuth()
  const tokens = useAppTokens()
  const sx = useMemo(() => buildStyles(tokens), [tokens])

  const newProjectTarget = user ? '/editor/new' : '/free'
  const year = new Date().getFullYear()

  const columns: FooterColumn[] = [
    {
      label: t('footer.productLabel'),
      links: [
        {to: '/', label: t('footer.home')},
        {to: '/features', label: t('footer.features')},
        {to: '/projects', label: t('footer.projects')},
        {to: '/pricing', label: t('footer.pricing')},
        {to: newProjectTarget, label: t('footer.newProject')},
      ],
    },
    {
      label: t('footer.companyLabel'),
      links: [
        {to: '/about', label: t('footer.about')},
        {to: '/contact', label: t('footer.contact')},
        {to: '/faq', label: t('footer.faq')},
      ],
    },
    {
      label: t('footer.guidesLabel'),
      links: [
        {to: '/guides/app-store-screenshot-sizes', label: t('footer.guideScreenshotSizes')},
        {to: '/guides/how-to-make-app-store-screenshots', label: t('footer.guideHowToMake')},
        {to: '/guides/app-store-screenshot-best-practices', label: t('footer.guideBestPractices')},
        {to: '/guides/google-play-feature-graphic', label: t('footer.guideFeatureGraphic')},
        {to: '/guides/iphone-18-iphone-ultra-app-store-screenshots', label: t('footer.guideFoldableIphone')},
        {to: GUIDES_INDEX_PATH, label: t('footer.moreGuides')},
      ],
    },
    {
      label: t('footer.legalLabel'),
      links: [
        {to: '/terms', label: t('footer.terms')},
        {to: '/privacy', label: t('footer.privacy')},
        {to: '/cookies', label: t('footer.cookies')},
        {to: '/refund', label: t('footer.refund')},
        {to: '/imprint', label: t('footer.imprint')},
      ],
    },
  ]

  return (
    <Box component="footer" role="contentinfo" sx={sx.root}>
      <Box aria-hidden sx={sx.glow} />

      <Box sx={sx.inner}>
        <Box sx={sx.brand}>
          <Box component={RouterLink} to="/" sx={sx.brandRow}>
            <BrandLogo markSize={34} fontSize={19} gap={12} />
          </Box>
          <Box component="p" sx={sx.tagline}>
            {t('footer.taglinePart1')}
            <br />
            <Box component="em" sx={sx.taglineEm}>
              {t('footer.taglinePart2')}
            </Box>
          </Box>
        </Box>

        <Box component="nav" aria-label={t('a11y.footer')} sx={sx.cols}>
          {columns.map((col) => (
            <Box key={col.label} sx={sx.col}>
              <Box sx={sx.colLabel}>{col.label}</Box>
              <Box component="ul" sx={sx.colList}>
                {col.links.map((link) => (
                  <Box component="li" key={link.to} sx={sx.colItem}>
                    <Box component={RouterLink} to={link.to} sx={sx.link}>
                      {link.label}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={sx.base}>
        <Box sx={sx.baseLeft}>
          <Box component="span">{t('footer.copyright', {year})}</Box>
        </Box>
        <Box sx={sx.baseRight}>
          <Box component={RouterLink} to="/terms" sx={sx.baseLink}>
            {t('footer.baseShortTerms')}
          </Box>
          <Box component={RouterLink} to="/privacy" sx={sx.baseLink}>
            {t('footer.baseShortPrivacy')}
          </Box>
          <Box component={RouterLink} to="/cookies" sx={sx.baseLink}>
            {t('footer.baseShortCookies')}
          </Box>
          <Box component={RouterLink} to="/imprint" sx={sx.baseLink}>
            {t('footer.baseShortImprint')}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    position: 'relative' as const,
    zIndex: 2,
    marginTop: '120px',
    padding: '72px 32px 28px',
    borderTop: `1px solid ${tokens.accordion.border}`,
    background:
      'linear-gradient(180deg, rgba(20, 17, 32, 0) 0%, rgba(20, 17, 32, 0.55) 50%, rgba(8, 6, 14, 0.85) 100%)',
    overflow: 'hidden' as const,
    color: tokens.text.secondary,
    [TABLET_BREAKPOINT]: {
      marginTop: '80px',
      padding: '56px 20px 24px',
    },
  },
  glow: {
    position: 'absolute' as const,
    left: '50%',
    top: 0,
    transform: 'translate(-50%, -50%)',
    width: '980px',
    height: '320px',
    background: `radial-gradient(ellipse 60% 60% at 50% 50%, ${tokens.accent.glow}, transparent 70%)`,
    filter: 'blur(40px)',
    pointerEvents: 'none' as const,
  },
  inner: {
    position: 'relative' as const,
    maxWidth: '1280px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: '1fr 2.6fr',
    gap: '48px',
    alignItems: 'start',
    [LAPTOP_BREAKPOINT]: {
      gridTemplateColumns: '1fr',
      gap: '40px',
    },
  },
  brand: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '18px',
  },
  brandRow: {
    display: 'inline-flex',
    alignItems: 'center',
    textDecoration: 'none',
    color: tokens.text.primary,
    width: 'fit-content',
  },
  tagline: {
    margin: 0,
    fontSize: '14px',
    lineHeight: 1.55,
    color: tokens.text.secondary,
    maxWidth: '34ch',
  },
  taglineEm: {
    fontStyle: 'italic' as const,
    fontWeight: 500,
    background: 'linear-gradient(180deg, #E5DCFF 0%, #A78BFA 100%)',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
  },
  cols: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '28px',
    [TABLET_BREAKPOINT]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '28px',
    },
    [PHONE_BREAKPOINT]: {
      gridTemplateColumns: '1fr',
      gap: '24px',
    },
  },
  col: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '14px',
  },
  colLabel: {
    fontSize: '10.5px',
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    color: tokens.text.muted,
  },
  colList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
  },
  colItem: {
    position: 'relative' as const,
  },
  link: {
    fontSize: '13.5px',
    color: tokens.text.secondary,
    textDecoration: 'none',
    transition: 'color 0.15s',
    display: 'inline-block',
    position: 'relative' as const,
    whiteSpace: 'nowrap' as const,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '-10px',
      top: '50%',
      width: '4px',
      height: '4px',
      borderRadius: '50%',
      background: tokens.accent.light,
      opacity: 0,
      transform: 'translateY(-50%) scale(0.6)',
      transition: 'opacity 0.15s, transform 0.15s',
      boxShadow: `0 0 6px ${tokens.accent.light}`,
    },
    '&:hover': {
      color: tokens.text.primary,
    },
    '&:hover::before': {
      opacity: 1,
      transform: 'translateY(-50%) scale(1)',
    },
  },
  base: {
    position: 'relative' as const,
    maxWidth: '1280px',
    margin: '56px auto 0',
    paddingTop: '22px',
    borderTop: `1px solid ${tokens.accordion.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    fontSize: '12px',
    color: tokens.text.muted,
    flexWrap: 'wrap' as const,
    [TABLET_BREAKPOINT]: {
      flexDirection: 'column' as const,
      alignItems: 'flex-start',
      gap: '12px',
    },
  },
  baseLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap' as const,
  },
  baseRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '18px',
    flexWrap: 'wrap' as const,
    [TABLET_BREAKPOINT]: {
      gap: '14px',
    },
  },
  baseLink: {
    color: tokens.text.muted,
    textDecoration: 'none',
    fontSize: '12px',
    transition: 'color 0.15s',
    '&:hover': {
      color: tokens.text.secondary,
    },
  },
})

export default Footer
